import React, { useState, useEffect } from 'react';
import { ChevronLeft, CheckCircle, AlertTriangle, XCircle, Star, Save, CheckCircle2 } from 'lucide-react';
import type { Skill } from './index';

interface SkillsEvaluationProps {
  position: string;
  candidateName: string;
  skills: Skill[];
  onComplete: (skills: Skill[], finalNotes: string, recommendation: 'approve' | 'reject') => void;
  onBack: () => void;
}

const evaluationOptions = [
  {
    value: 'not_met',
    label: 'Não atende',
    icon: XCircle,
    color: 'text-red-600 bg-red-50 hover:bg-red-100 border-red-200',
  },
  {
    value: 'partially_met',
    label: 'Atende parcialmente',
    icon: AlertTriangle,
    color: 'text-yellow-600 bg-yellow-50 hover:bg-yellow-100 border-yellow-200',
  },
  {
    value: 'fully_met',
    label: 'Atende completamente',
    icon: CheckCircle,
    color: 'text-green-600 bg-green-50 hover:bg-green-100 border-green-200',
  },
  {
    value: 'exceeds',
    label: 'Excede',
    icon: Star,
    color: 'text-purple-600 bg-purple-50 hover:bg-purple-100 border-purple-200',
  },
] as const;

export const SkillsEvaluation: React.FC<SkillsEvaluationProps> = ({
  position,
  candidateName,
  skills: initialSkills,
  onComplete,
  onBack,
}) => {
  const [skills, setSkills] = useState(initialSkills);
  const [finalNotes, setFinalNotes] = useState('');
  const [recommendation, setRecommendation] = useState<'approve' | 'reject' | null>(null);
  const [expandedSkill, setExpandedSkill] = useState<string | null>(null);
  const [showProgress, setShowProgress] = useState(true);

  const handleEvaluationChange = (skillId: string, evaluation: typeof evaluationOptions[number]['value']) => {
    setSkills(prev => prev.map(skill => 
      skill.id === skillId ? { ...skill, evaluation } : skill
    ));
  };

  const handleNotesChange = (skillId: string, notes: string) => {
    setSkills(prev => prev.map(skill => 
      skill.id === skillId ? { ...skill, notes } : skill
    ));
  };

  const evaluatedSkills = skills.filter(skill => skill.evaluation).length;
  const totalSkills = skills.length;
  const progress = Math.round((evaluatedSkills / totalSkills) * 100);

  const isComplete = skills.every(skill => skill.evaluation) && recommendation !== null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isComplete && recommendation) {
      onComplete(skills, finalNotes, recommendation);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowProgress(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="bg-white rounded-lg shadow-md relative">
      {/* Floating Progress Indicator */}
      <div 
        className={`fixed bottom-4 right-4 transition-opacity duration-300 ${
          showProgress ? 'opacity-100' : 'opacity-0 hover:opacity-100'
        }`}
      >
        <div className="bg-white rounded-lg shadow-lg p-4 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <CheckCircle2 
                className={`w-5 h-5 ${progress === 100 ? 'text-green-600' : 'text-blue-600'}`}
              />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                {progress === 100 ? 'Avaliação Completa!' : 'Progresso'}
              </p>
              <p className="text-xs text-gray-500">
                {evaluatedSkills} de {totalSkills} skills avaliadas
              </p>
            </div>
          </div>
          <div className="w-16 h-16 relative">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="32"
                cy="32"
                r="24"
                strokeWidth="8"
                stroke="#E5E7EB"
                fill="none"
              />
              <circle
                cx="32"
                cy="32"
                r="24"
                strokeWidth="8"
                stroke={progress === 100 ? '#22C55E' : '#3B82F6'}
                fill="none"
                strokeDasharray={`${2 * Math.PI * 24}`}
                strokeDashoffset={`${2 * Math.PI * 24 * (1 - progress / 100)}`}
                className="transition-all duration-500"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-sm font-medium">{progress}%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 border-b">
        <button
          onClick={onBack}
          className="text-gray-600 hover:text-gray-800 transition-colors flex items-center gap-2 mb-4"
        >
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </button>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Avaliação Técnica
        </h1>
        <div className="text-gray-600">
          <p className="font-medium">{position}</p>
          <p>{candidateName}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-8">
        <div className="space-y-6">
          {skills.map((skill) => (
            <div key={skill.id} className="border rounded-lg overflow-hidden">
              <div className="p-4 bg-gray-50">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">
                      {skill.name}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {skill.description}
                    </p>
                  </div>
                  
                  {skill.evaluation && (
                    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-sm ${
                      evaluationOptions.find(opt => opt.value === skill.evaluation)?.color
                    }`}>
                      {React.createElement(
                        evaluationOptions.find(opt => opt.value === skill.evaluation)?.icon || 'span',
                        { className: 'w-4 h-4' }
                      )}
                      <span className="text-xs font-medium">
                        {evaluationOptions.find(opt => opt.value === skill.evaluation)?.label}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  {evaluationOptions.map((option) => {
                    const Icon = option.icon;
                    const isSelected = skill.evaluation === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleEvaluationChange(skill.id, option.value)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors border ${
                          isSelected
                            ? option.color
                            : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{option.label}</span>
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={() => setExpandedSkill(expandedSkill === skill.id ? null : skill.id)}
                  className="text-sm text-blue-600 hover:text-blue-800 mt-4"
                >
                  {expandedSkill === skill.id ? 'Ocultar observações' : 'Adicionar observações'}
                </button>
              </div>

              {expandedSkill === skill.id && (
                <div className="p-4 border-t">
                  <textarea
                    value={skill.notes || ''}
                    onChange={(e) => handleNotesChange(skill.id, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder="Adicione observações específicas sobre esta skill"
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="space-y-4 pt-6 border-t">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Considerações Finais
            </label>
            <textarea
              value={finalNotes}
              onChange={(e) => setFinalNotes(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              rows={4}
              placeholder="Adicione suas considerações finais sobre o candidato"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Recomendação
            </label>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setRecommendation('approve')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md border transition-colors ${
                  recommendation === 'approve'
                    ? 'border-green-600 bg-green-50 text-green-700'
                    : 'border-gray-200 hover:bg-gray-50 text-gray-700'
                }`}
              >
                <CheckCircle className="w-4 h-4" />
                <span>Continuar</span>
              </button>

              <button
                type="button"
                onClick={() => setRecommendation('reject')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md border transition-colors ${
                  recommendation === 'reject'
                    ? 'border-red-600 bg-red-50 text-red-700'
                    : 'border-gray-200 hover:bg-gray-50 text-gray-700'
                }`}
              >
                <XCircle className="w-4 h-4" />
                <span>Encerrar</span>
              </button>
            </div>
          </div>
        </div>

        <div className="pt-6">
          <button
            type="submit"
            disabled={!isComplete}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            Finalizar Avaliação
          </button>
        </div>
      </form>
    </div>
  );
};